import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { useQuery } from 'react-query';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import {
  Container, Grid, Card, CardContent, Typography, Button, Box,
  TextField, Chip, Paper, CircularProgress, Alert,
  Divider, Dialog, DialogTitle,
  DialogContent, DialogActions, IconButton, ImageList, ImageListItem,
  ImageListItemBar, Autocomplete, Drawer, useMediaQuery, useTheme,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel,
} from '@mui/material';
import {
  Search as SearchIcon, FilterList as FilterIcon,
  Close as CloseIcon,
  Download as DownloadIcon, OpenInNew as OpenInNewIcon,
  TuneOutlined as TuneIcon,
} from '@mui/icons-material';
import { pagesAPI, issuesAPI, photosAPI } from '../services/api';
import { sortContributorsBySequence, contributorKey } from '../utils/contributorSort';
import { colors } from '../theme';

const FILTER_DRAWER_WIDTH = 300;

const localeSort = (a, b) =>
  String(a).localeCompare(String(b), undefined, { sensitivity: 'base', numeric: true });

const sortAlpha = (items, getKey = (item) => item) =>
  [...items].sort((a, b) => localeSort(getKey(a), getKey(b)));

const SERVICE_TYPES = [
  '兒童癌症', '糖尿病', '災難援助', '環境保育',
  '人道主義', '對抗飢餓', '視覺服務', '青少年',
];

const parseMulti = (params, key) => {
  const v = params.get(key);
  return v ? v.split(',').filter(Boolean) : [];
};

// clubs is an array of name_zh strings
const buildParams = (mode, query, years, serviceTypes, clubs, beneficiaries, partners, isExcellent, pg = 1, pgSize = 30) => {
  const p = { mode };
  if (query) p.q = query;
  if (years.length) p.year = years.join(',');
  if (serviceTypes.length) p.service_type = serviceTypes.join(',');
  if (clubs.length) p.club = clubs.join(',');
  if (beneficiaries.length) p.beneficiary = beneficiaries.join(',');
  if (partners.length) p.partner = partners.join(',');
  if (isExcellent === '是') p.is_excellent = 'true';
  else if (isExcellent === '否') p.is_excellent = 'false';
  if (pg > 1) p.page = String(pg);
  if (pgSize !== 30) p.page_size = String(pgSize);
  return p;
};

// Hook for infinite-scroll autocomplete backed by a paginated API
function useInfiniteOptions(fetchFn, { getSortKey, sortFn } = {}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const stateRef = useRef({ page: 1, hasMore: false, loading: false, input: '' });
  const fetchFnRef = useRef(fetchFn);
  fetchFnRef.current = fetchFn;
  const getSortKeyRef = useRef(getSortKey);
  getSortKeyRef.current = getSortKey;
  const sortFnRef = useRef(sortFn);
  sortFnRef.current = sortFn;

  const sortItems = useCallback((items) => {
    if (sortFnRef.current) return sortFnRef.current(items);
    return sortAlpha(items, getSortKeyRef.current || ((item) => item));
  }, []);

  const fetch = useCallback(async (q, pg) => {
    if (stateRef.current.loading) return;
    stateRef.current.loading = true;
    setLoading(true);
    try {
      const res = await fetchFnRef.current(q, pg);
      const items = (res.data.results || []).filter(Boolean).filter((v) => v !== 'None');
      if (pg === 1) setOptions(sortItems(items));
      else {
        setOptions((prev) => {
          const seen = new Set(prev.map(contributorKey));
          const merged = [...prev, ...items.filter((i) => !seen.has(contributorKey(i)))];
          return sortItems(merged);
        });
      }
      stateRef.current.hasMore = res.data.has_more ?? false;
      stateRef.current.page = pg;
    } finally {
      stateRef.current.loading = false;
      setLoading(false);
    }
  }, [sortItems]);

  const onInputChange = useCallback((_, value, reason) => {
    if (reason === 'input') {
      stateRef.current.input = value;
      fetch(value, 1);
    }
  }, [fetch]);

  const onOpen = useCallback(() => {
    fetch(stateRef.current.input, 1);
  }, [fetch]);

  const listboxProps = useMemo(() => ({
    onScroll: (e) => {
      const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
      if (
        scrollHeight - scrollTop - clientHeight < 80 &&
        stateRef.current.hasMore &&
        !stateRef.current.loading
      ) {
        fetch(stateRef.current.input, stateRef.current.page + 1);
      }
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [fetch]);

  return { options, loading, onInputChange, onOpen, listboxProps };
}

// Treat Python 'None' string and falsy values as empty
const val = (v) => (!v || v === 'None' ? '' : v);

const SearchPage = () => {
  const { t, i18n } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [selectedYears, setSelectedYears] = useState(() => parseMulti(searchParams, 'year'));
  const [selectedServiceTypes, setSelectedServiceTypes] = useState(() => parseMulti(searchParams, 'service_type'));
  const [selectedClubs, setSelectedClubs] = useState(() => parseMulti(searchParams, 'club'));
  const [selectedBeneficiaries, setSelectedBeneficiaries] = useState(() => parseMulti(searchParams, 'beneficiary'));
  const [selectedPartners, setSelectedPartners] = useState(() => parseMulti(searchParams, 'partner'));
  const [isExcellent, setIsExcellent] = useState(() => {
    const v = searchParams.get('is_excellent');
    return v === 'true' ? '是' : v === 'false' ? '否' : '';
  });
  const [viewMode, setViewMode] = useState(searchParams.get('mode') || 'table');
  const [page, setPage] = useState(() => parseInt(searchParams.get('page') || '1', 10));
  const [pageSize, setPageSize] = useState(() => parseInt(searchParams.get('page_size') || '30', 10));
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');

  useEffect(() => {
    setSearchQuery(searchParams.get('q') || '');
    setSelectedYears(parseMulti(searchParams, 'year'));
    setSelectedServiceTypes(parseMulti(searchParams, 'service_type'));
    setSelectedClubs(parseMulti(searchParams, 'club'));
    setSelectedBeneficiaries(parseMulti(searchParams, 'beneficiary'));
    setSelectedPartners(parseMulti(searchParams, 'partner'));
    setIsExcellent(() => {
      const v = searchParams.get('is_excellent');
      return v === 'true' ? '是' : v === 'false' ? '否' : '';
    });
    setViewMode(searchParams.get('mode') || 'table');
    setPage(parseInt(searchParams.get('page') || '1', 10));
    setPageSize(parseInt(searchParams.get('page_size') || '30', 10));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const activeFilters = {
    q: searchQuery,
    year: selectedYears.join(','),
    service_type: selectedServiceTypes.join(','),
    club: selectedClubs.join(','),
    beneficiary: selectedBeneficiaries.join(','),
    partner: selectedPartners.join(','),
    is_excellent: isExcellent === '是' ? 'true' : isExcellent === '否' ? 'false' : '',
    page,
    page_size: pageSize,
  };

  // Derive directly from URL so cache is hit immediately on remount (before useEffect syncs state)
  const urlFilters = {
    q: searchParams.get('q') || '',
    year: searchParams.get('year') || '',
    service_type: searchParams.get('service_type') || '',
    club: searchParams.get('club') || '',
    beneficiary: searchParams.get('beneficiary') || '',
    partner: searchParams.get('partner') || '',
    is_excellent: searchParams.get('is_excellent') || '',
    page,
    page_size: pageSize,
  };

  const hasAnyFilter = !!(
    urlFilters.q || urlFilters.year ||
    urlFilters.service_type || urlFilters.club ||
    urlFilters.beneficiary || urlFilters.partner || urlFilters.is_excellent
  );

  const urlViewMode = searchParams.get('mode') || 'table';

  const { data: pagesData, isLoading: pagesLoading, error: pagesError } = useQuery(
    ['pageSearch', urlFilters],
    () => pagesAPI.search(urlFilters),
    { keepPreviousData: true, staleTime: 30000, enabled: (urlViewMode === 'page' || urlViewMode === 'table') && hasAnyFilter }
  );

  const { data: photosData, isLoading: photosLoading, error: photosError } = useQuery(
    ['photoSearch', urlFilters],
    () => photosAPI.search(urlFilters),
    { keepPreviousData: true, staleTime: 30000, enabled: urlViewMode === 'photo' && hasAnyFilter }
  );

  const { data: yearsData } = useQuery('years', issuesAPI.getYears, { staleTime: 300000 });

  const clubAutoFill = useInfiniteOptions(
    (q, pg) => pagesAPI.getClubs(q, pg, 200),
    { sortFn: sortContributorsBySequence },
  );
  const serviceTypeAutoFill = useInfiniteOptions((q, pg) => pagesAPI.getServiceTypes(q, pg, 50));

  // Build lookup map: name_zh → contributor object (for label formatting)
  const clubLookup = useMemo(() => {
    const map = {};
    clubAutoFill.options.forEach((opt) => {
      if (opt && opt.name_zh) map[opt.name_zh] = opt;
    });
    return map;
  }, [clubAutoFill.options]);

  const formatClubLabel = useCallback((nameZh) => {
    const club = clubLookup[nameZh];
    if (!club) return nameZh;
    const name = i18n.language === 'zh' ? club.name_zh : (club.name_en || club.name_zh);
    return `(${club.short_form}) ${name}`;
  }, [clubLookup, i18n.language]);
  const beneficiaryAutoFill = useInfiniteOptions((q) => pagesAPI.getBeneficiaries(q).then((res) => ({ data: { results: res.data, has_more: false } })));
  const partnerAutoFill = useInfiniteOptions((q, pg) => pagesAPI.getPartners(q, pg, 50));

  const handlePageChange = (newPage) => {
    setPage(newPage);
    setSearchParams(buildParams(
      viewMode, searchQuery,
      selectedYears, selectedServiceTypes,
      selectedClubs, selectedBeneficiaries, selectedPartners,
      isExcellent, newPage, pageSize
    ));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedYears([]);
    setSelectedServiceTypes([]);
    setSelectedClubs([]);
    setSelectedBeneficiaries([]);
    setSelectedPartners([]);
    setIsExcellent('');
    setPage(1);
    setSearchParams({ mode: viewMode });
  };

  const handleViewModeChange = (_, newMode) => {
    if (!newMode) return;
    setViewMode(newMode);
    setPage(1);
    setSearchParams(buildParams(
      newMode, searchQuery,
      selectedYears, selectedServiceTypes,
      selectedClubs, selectedBeneficiaries, selectedPartners,
      isExcellent, 1, pageSize
    ));
  };

  const clubDisplay = (name) => val(name) || '區會';

  const handleDownloadPDF = async () => {
    const results = pagesData?.data?.results || [];
    if (!results.length) return;

    const q = searchParams.get('q') || '';
    const date = new Date().toLocaleDateString('zh-TW');
    const esc = (s) => String(s == null || s === 'None' ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const origin = window.location.origin;

    const cols = [t('colIssue'), t('colClub'), t('colEventName'), t('colDate'), t('colLocation'), t('colBeneficiary'), t('colPartner'), t('colKeywords'), t('colSummary'), t('colLink')];
    const colWidths = ['10%', '10%', '12%', '6%', '9%', '8%', '9%', '13%', '13%', '10%'];

    const headerCells = cols.map((h) =>
      `<th style="background:#1a367e;color:#fff;padding:5px 6px;border:1px solid #aaa;white-space:nowrap;font-weight:bold;text-align:left">${h}</th>`
    ).join('');

    const bodyRows = results.map((item, i) => {
      const bg = i % 2 === 1 ? '#f5f7ff' : '#fff';
      const td = (content, extra = '') =>
        `<td style="border:1px solid #ccc;padding:4px 5px;word-break:break-word;overflow-wrap:break-word;background:${bg};vertical-align:top${extra}">${content}</td>`;
      const pageUrl = `${origin}/pages/${item.id}`;
      const linkTd = `<td style="border:1px solid #ccc;padding:4px 5px;background:${bg};vertical-align:top;font-size:9px">
        <a href="${pageUrl}" data-pdf-link style="color:#1a367e;text-decoration:underline;word-break:break-word;overflow-wrap:break-word">${pageUrl}</a>
      </td>`;
      return `<tr>
        ${td(esc(item.issue_title))}
        ${td(esc(clubDisplay(item.club_name)))}
        ${td(esc(item.event_name))}
        ${td(esc(item.event_date))}
        ${td(esc(item.event_location))}
        ${td(esc(item.beneficiary))}
        ${td(esc(item.co_organizer))}
        ${td(esc((item.keyword_refs || []).map((k) => k.name).join(', ')))}
        ${td(esc(item.summary))}
        ${linkTd}
      </tr>`;
    }).join('');

    const colgroup = colWidths.map((w) => `<col style="width:${w}">`).join('');
    const container = document.createElement('div');
    container.style.cssText = [
      'position:fixed', 'left:-9999px', 'top:0',
      'width:1122px',
      'background:#fff', 'padding:16px 20px',
      "font-family:'PingFang TC','Microsoft JhengHei','Noto Sans TC',sans-serif",
      'font-size:10px', 'color:#111',
    ].join(';');
    container.innerHTML = `
      <div style="margin-bottom:8px">
        <div style="font-size:13px;font-weight:700;margin-bottom:2px">搜尋結果${q ? `：${q}` : ''}</div>
        <div style="font-size:9px;color:#555">共 ${results.length} 筆結果　${date}</div>
      </div>
      <table id="pdfTable" style="width:100%;border-collapse:collapse;table-layout:fixed">
        <colgroup>${colgroup}</colgroup>
        <thead><tr>${headerCells}</tr></thead>
        <tbody>${bodyRows}</tbody>
      </table>`;

    document.body.appendChild(container);
    try {
      await new Promise((r) => setTimeout(r, 200));
      const scale = 2;
      const containerRect = container.getBoundingClientRect();
      const pdfLinks = Array.from(container.querySelectorAll('a[data-pdf-link]')).map((a) => {
        const r = a.getBoundingClientRect();
        return {
          url: a.href,
          left: (r.left - containerRect.left) * scale,
          top: (r.top - containerRect.top) * scale,
          width: r.width * scale,
          height: r.height * scale,
        };
      });
      const canvas = await html2canvas(container, { scale, useCORS: true, logging: false });

      // Collect the bottom edge of every row in canvas-pixel space
      const rowEnds = Array.from(container.querySelectorAll('#pdfTable tr')).map((tr) => {
        const r = tr.getBoundingClientRect();
        return Math.round((r.bottom - containerRect.top) * scale);
      });

      document.body.removeChild(container);

      // Slice canvas at row boundaries so no row is ever split across pages
      const pageH = Math.round(794 * scale); // A4 landscape height × scale
      const slices = [];
      let start = 0;
      while (start < canvas.height) {
        const ideal = start + pageH;
        const cut = rowEnds.filter((e) => e > start && e <= ideal).pop()
          ?? Math.min(ideal, canvas.height);
        slices.push({ start, end: Math.min(cut, canvas.height) });
        start = Math.min(cut, canvas.height);
      }

      const doc = new jsPDF({ orientation: 'landscape', unit: 'px', format: 'a4' });
      const pdfW = doc.internal.pageSize.getWidth();
      slices.forEach((slice, idx) => {
        if (idx > 0) doc.addPage();
        const h = slice.end - slice.start;
        const sc = document.createElement('canvas');
        sc.width = canvas.width;
        sc.height = h;
        sc.getContext('2d').drawImage(canvas, 0, -slice.start);
        const pageImgH = (h / canvas.width) * pdfW;
        doc.addImage(sc.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, pdfW, pageImgH);

        pdfLinks
          .filter((link) => link.top + link.height > slice.start && link.top < slice.end)
          .forEach((link) => {
            const x = (link.left / canvas.width) * pdfW;
            const y = ((Math.max(link.top, slice.start) - slice.start) / h) * pageImgH;
            const w = (link.width / canvas.width) * pdfW;
            const linkH = ((Math.min(link.top + link.height, slice.end) - Math.max(link.top, slice.start)) / h) * pageImgH;
            doc.link(x, y, w, linkH, { url: link.url });
          });
      });

      doc.save(`search_results_${new Date().toISOString().split('T')[0]}.pdf`);
    } catch (err) {
      if (document.body.contains(container)) document.body.removeChild(container);
      console.error('PDF generation failed', err);
    }
  };

  const handleSortClick = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const sortedResults = useMemo(() => {
    const results = [...(pagesData?.data?.results || [])];
    if (!sortField) return results;
    return results.sort((a, b) => {
      const av = val(a[sortField]) || '';
      const bv = val(b[sortField]) || '';
      const cmp = av.localeCompare(bv, undefined, { sensitivity: 'base' });
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [pagesData?.data?.results, sortField, sortDir]);

  const activeFilterCount =
    selectedYears.length + selectedServiceTypes.length +
    selectedClubs.length + selectedBeneficiaries.length + selectedPartners.length +
    (isExcellent ? 1 : 0);

  const isLoading = (viewMode === 'page' || viewMode === 'table') ? pagesLoading : photosLoading;
  const error = (viewMode === 'page' || viewMode === 'table') ? pagesError : photosError;
  const resultsCount =
    (viewMode === 'page' || viewMode === 'table')
      ? (pagesData?.data?.count || 0)
      : (photosData?.data?.count || 0);
  const yearOptions = useMemo(
    () => [...(yearsData?.data || [])].map(String).sort((a, b) => Number(b) - Number(a)),
    [yearsData],
  );

  const FilterPanel = () => (
    <Box sx={{ p: 2, maxHeight: 'calc(100vh - 100px)', overflowY: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FilterIcon sx={{ mr: 1, color: colors.royalBlue }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('filterTitle')}</Typography>
        </Box>
        {activeFilterCount > 0 && (
          <Chip
            label={activeFilterCount}
            size="small"
            color="primary"
            sx={{ minWidth: 24, height: 24 }}
          />
        )}
      </Box>
      <Divider sx={{ mb: 2 }} />

      {/* Year */}
      <Autocomplete
        multiple
        size="small"
        options={yearOptions}
        value={selectedYears}
        sortSelectedOptions
        onChange={(_, v) => {
          setSelectedYears(v);
          setPage(1);
          setSearchParams(buildParams(viewMode, searchQuery, v, selectedServiceTypes, selectedClubs, selectedBeneficiaries, selectedPartners, isExcellent, 1, pageSize));
        }}
        filterSelectedOptions
        renderTags={(val, getTagProps) =>
          val.map((opt, idx) => (
            <Chip label={opt} size="small" {...getTagProps({ index: idx })} key={opt} />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} label={t('yearLabel')} placeholder={selectedYears.length ? '' : t('selectYear')} />
        )}
        sx={{ mb: 2 }}
      />

      {/* Club / Contributor */}
      <Autocomplete
        multiple
        size="small"
        options={clubAutoFill.options}
        value={selectedClubs}
        onOpen={clubAutoFill.onOpen}
        onInputChange={clubAutoFill.onInputChange}
        ListboxProps={clubAutoFill.listboxProps}
        loading={clubAutoFill.loading}
        getOptionLabel={(opt) => {
          if (typeof opt === 'string') return formatClubLabel(opt);
          const name = i18n.language === 'zh' ? opt.name_zh : (opt.name_en || opt.name_zh);
          return `(${opt.short_form}) ${name}`;
        }}
        isOptionEqualToValue={(opt, val) => {
          const valStr = typeof val === 'string' ? val : val.name_zh;
          return opt.name_zh === valStr;
        }}
        onChange={(_, v) => {
          const names = v.map((x) => (typeof x === 'string' ? x : x.name_zh));
          setSelectedClubs(names);
          setPage(1);
          setSearchParams(buildParams(viewMode, searchQuery, selectedYears, selectedServiceTypes, names, selectedBeneficiaries, selectedPartners, isExcellent, 1, pageSize));
        }}
        filterSelectedOptions
        filterOptions={(x) => x}
        renderTags={(val, getTagProps) =>
          val.map((opt, idx) => {
            const key = typeof opt === 'string' ? opt : opt.name_zh;
            return <Chip label={formatClubLabel(key)} size="small" {...getTagProps({ index: idx })} key={key} />;
          })
        }
        renderInput={(params) => (
          <TextField {...params} label={t('clubLabel')} placeholder={selectedClubs.length ? '' : t('enterClub')} />
        )}
        sx={{ mb: 2 }}
      />

      {/* Service Type */}
      <Autocomplete
        multiple
        size="small"
        options={serviceTypeAutoFill.options}
        value={selectedServiceTypes}
        sortSelectedOptions
        onOpen={serviceTypeAutoFill.onOpen}
        onInputChange={serviceTypeAutoFill.onInputChange}
        ListboxProps={serviceTypeAutoFill.listboxProps}
        loading={serviceTypeAutoFill.loading}
        onChange={(_, v) => {
          setSelectedServiceTypes(v);
          setPage(1);
          setSearchParams(buildParams(viewMode, searchQuery, selectedYears, v, selectedClubs, selectedBeneficiaries, selectedPartners, isExcellent, 1, pageSize));
        }}
        filterSelectedOptions
        filterOptions={(x) => x}
        renderTags={(val, getTagProps) =>
          val.map((opt, idx) => (
            <Chip label={opt} size="small" {...getTagProps({ index: idx })} key={opt} />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} label={t('serviceType')} placeholder={selectedServiceTypes.length ? '' : t('selectServiceType')} />
        )}
        sx={{ mb: 2 }}
      />

      {/* Beneficiary */}
      <Autocomplete
        multiple
        size="small"
        options={beneficiaryAutoFill.options}
        value={selectedBeneficiaries}
        sortSelectedOptions
        onOpen={beneficiaryAutoFill.onOpen}
        onInputChange={beneficiaryAutoFill.onInputChange}
        ListboxProps={beneficiaryAutoFill.listboxProps}
        loading={beneficiaryAutoFill.loading}
        onChange={(_, v) => {
          setSelectedBeneficiaries(v);
          setPage(1);
          setSearchParams(buildParams(viewMode, searchQuery, selectedYears, selectedServiceTypes, selectedClubs, v, selectedPartners, isExcellent, 1, pageSize));
        }}
        filterSelectedOptions
        filterOptions={(x) => x}
        renderTags={(val, getTagProps) =>
          val.map((opt, idx) => (
            <Chip label={opt} size="small" {...getTagProps({ index: idx })} key={opt} />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} label={t('beneficiaryLabel')} placeholder={selectedBeneficiaries.length ? '' : t('enterSearch')} />
        )}
        sx={{ mb: 2 }}
      />

      {/* Partner */}
      <Autocomplete
        multiple
        size="small"
        options={partnerAutoFill.options}
        value={selectedPartners}
        sortSelectedOptions
        onOpen={partnerAutoFill.onOpen}
        onInputChange={partnerAutoFill.onInputChange}
        ListboxProps={partnerAutoFill.listboxProps}
        loading={partnerAutoFill.loading}
        onChange={(_, v) => {
          setSelectedPartners(v);
          setPage(1);
          setSearchParams(buildParams(viewMode, searchQuery, selectedYears, selectedServiceTypes, selectedClubs, selectedBeneficiaries, v, isExcellent, 1, pageSize));
        }}
        filterSelectedOptions
        filterOptions={(x) => x}
        renderTags={(val, getTagProps) =>
          val.map((opt, idx) => (
            <Chip label={opt} size="small" {...getTagProps({ index: idx })} key={opt} />
          ))
        }
        renderInput={(params) => (
          <TextField {...params} label={t('partnerLabel')} placeholder={selectedPartners.length ? '' : t('enterSearch')} />
        )}
        sx={{ mb: 2 }}
      />

      {/* 優 Excellence badge */}
      <Autocomplete
        size="small"
        options={['是', '否']}
        value={isExcellent || null}
        onChange={(_, v) => {
          const next = v || '';
          setIsExcellent(next);
          setPage(1);
          setSearchParams(buildParams(viewMode, searchQuery, selectedYears, selectedServiceTypes, selectedClubs, selectedBeneficiaries, selectedPartners, next, 1, pageSize));
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Box component="span" sx={{ fontWeight: 700, color: colors.gold }}>優</Box>
                <Box component="span">{t('excellentOnly')}</Box>
              </Box>
            }
          />
        )}
        sx={{ mb: 2 }}
      />

      {activeFilterCount > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Button
            variant="outlined"
            fullWidth
            onClick={clearFilters}
            sx={{ borderColor: colors.royalBlue, color: colors.royalBlue }}
          >
            {t('clearFilters')}
          </Button>
        </>
      )}
    </Box>
  );

  return (
    <Box sx={{ backgroundColor: colors.lightGray, minHeight: '100vh' }}>
      {/* Search Header */}
      <Paper
        elevation={0}
        sx={{
          position: 'relative',
          backgroundImage: 'url(/hero-bg.jpeg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          p: 4,
          borderRadius: 0,
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: `linear-gradient(135deg, ${colors.royalBlue}D0 0%, #1565C0CC 100%)`,
          },
        }}
      >
        <Container maxWidth="xl" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
            <Box>
              <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
                {t('searchPageTitle')}
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                {t('searchPageSubtitle')}
              </Typography>
            </Box>
            {isMobile && (
              <IconButton
                onClick={() => setMobileFilterOpen(true)}
                sx={{ color: 'white', flexShrink: 0, position: 'relative' }}
              >
                <TuneIcon />
                {activeFilterCount > 0 && (
                  <Box
                    sx={{
                      position: 'absolute', top: 4, right: 4,
                      width: 16, height: 16, borderRadius: '50%',
                      backgroundColor: colors.gold,
                      fontSize: '10px', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', color: 'white',
                    }}
                  >
                    {activeFilterCount}
                  </Box>
                )}
              </IconButton>
            )}
          </Box>

          {/* Active filter chips */}
          {activeFilterCount > 0 && (
            <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {sortAlpha([
                ...selectedYears.map((y) => ({
                  label: y,
                  rm: () => setSelectedYears((p) => p.filter((v) => v !== y)),
                })),
                ...selectedServiceTypes.map((s) => ({
                  label: s,
                  rm: () => setSelectedServiceTypes((p) => p.filter((v) => v !== s)),
                })),
                ...selectedClubs.map((c) => ({
                  label: formatClubLabel(c),
                  rm: () => setSelectedClubs((p) => p.filter((v) => v !== c)),
                })),
                ...selectedBeneficiaries.map((b) => ({
                  label: b,
                  rm: () => {
                    const next = selectedBeneficiaries.filter((v) => v !== b);
                    setSelectedBeneficiaries(next);
                    setPage(1);
                    setSearchParams(buildParams(viewMode, searchQuery, selectedYears, selectedServiceTypes, selectedClubs, next, selectedPartners, isExcellent, 1, pageSize));
                  },
                })),
                ...selectedPartners.map((pt) => ({
                  label: pt,
                  rm: () => {
                    const next = selectedPartners.filter((v) => v !== pt);
                    setSelectedPartners(next);
                    setPage(1);
                    setSearchParams(buildParams(viewMode, searchQuery, selectedYears, selectedServiceTypes, selectedClubs, selectedBeneficiaries, next, isExcellent, 1, pageSize));
                  },
                })),
                ...(isExcellent ? [{
                  label: `優 ${isExcellent}`,
                  rm: () => {
                    setIsExcellent('');
                    setPage(1);
                    setSearchParams(buildParams(viewMode, searchQuery, selectedYears, selectedServiceTypes, selectedClubs, selectedBeneficiaries, selectedPartners, '', 1, pageSize));
                  },
                }] : []),
              ], (item) => item.label).map(({ label, rm }, i) => (
                <Chip
                  key={i}
                  label={label}
                  size="small"
                  onDelete={rm}
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    color: 'white',
                    '& .MuiChip-deleteIcon': { color: 'rgba(255,255,255,0.7)' },
                  }}
                />
              ))}
            </Box>
          )}
        </Container>
      </Paper>

      {/* Mobile Drawer */}
      <Drawer
        anchor="left"
        open={mobileFilterOpen}
        onClose={() => setMobileFilterOpen(false)}
        sx={{ display: { md: 'none' } }}
      >
        <Box sx={{ width: FILTER_DRAWER_WIDTH }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{t('filterTitle')}</Typography>
            <IconButton onClick={() => setMobileFilterOpen(false)}><CloseIcon /></IconButton>
          </Box>
          {FilterPanel()}
        </Box>
      </Drawer>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* Desktop Sidebar */}
          <Paper
            sx={{
              width: FILTER_DRAWER_WIDTH,
              flexShrink: 0,
              borderRadius: 2,
              display: { xs: 'none', md: 'block' },
              height: 'fit-content',
              position: 'sticky',
              top: 80,
            }}
          >
            {FilterPanel()}
          </Paper>

          {/* Results Area */}
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Box
              sx={{
                mb: 3, display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap', gap: 2,
              }}
            >
              {hasAnyFilter && (
                <Typography variant="body1" color="text.secondary">
                  {t('resultsCount', { count: resultsCount })}
                </Typography>
              )}
            </Box>

            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mb: 4 }}>
                {t('searchError')}
              </Alert>
            )}

            {/* Empty state – no filters applied */}
            {!isLoading && !error && !hasAnyFilter && (
              <Box sx={{ textAlign: 'center', py: 10, color: 'text.secondary' }}>
                <SearchIcon sx={{ fontSize: 64, opacity: 0.2, mb: 2 }} />
                <Typography>{t('enterFiltersToSearch')}</Typography>
              </Box>
            )}

            {/* No results */}
            {!isLoading && !error && hasAnyFilter && resultsCount === 0 && (
              <Alert severity="info">{t('noResultsMatch')}</Alert>
            )}

            {/* ── Table View ── */}
            {!isLoading && !error && hasAnyFilter && viewMode === 'table' && (pagesData?.data?.results?.length > 0) && (
              <>
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleDownloadPDF}
                    sx={{ borderColor: colors.royalBlue, color: colors.royalBlue }}
                  >
                    {t('download')}
                  </Button>
                </Box>
                {isMobile ? (
                  <Box>
                    {sortedResults.map((item) => (
                      <Paper key={item.id} sx={{ mb: 1.5, p: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 0.5 }}>
                          {val(item.issue_title)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {val(item.club_name) || t('district')}
                        </Typography>
                        {val(item.event_name) && (
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            {val(item.event_name)}
                          </Typography>
                        )}
                        {val(item.service_type) && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            {val(item.service_type)}
                          </Typography>
                        )}
                        {val(item.summary) && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.8rem', lineHeight: 1.5 }}>
                            {val(item.summary)}
                          </Typography>
                        )}
                        <Button
                          component={RouterLink}
                          to={`/pages/${item.id}`}
                          size="small"
                          sx={{ mt: 1, color: colors.royalBlue, p: 0 }}
                          endIcon={<OpenInNewIcon />}
                        >
                          {t('view')}
                        </Button>
                      </Paper>
                    ))}
                  </Box>
                ) : (
                  <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                    <Table size="small" sx={{ tableLayout: 'fixed' }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: colors.royalBlue }}>
                          {[
                            { label: t('colIssue'), field: 'issue_title', w: '14%' },
                            { label: t('colClub'), field: 'club_name', w: '15%' },
                            { label: t('colEventName'), field: 'event_name', w: '19%' },
                            { label: t('colServiceType'), field: 'service_type', w: '17%' },
                            { label: t('colSummary'), field: 'summary', w: '29%' },
                            { label: t('colLink'), field: null, w: '6%' },
                          ].map(({ label, field, w }) => (
                            <TableCell key={label} sx={{ color: 'white', fontWeight: 600, width: w }}>
                              {field ? (
                                <TableSortLabel
                                  active={sortField === field}
                                  direction={sortField === field ? sortDir : 'asc'}
                                  onClick={() => handleSortClick(field)}
                                  sx={{
                                    color: 'white !important',
                                    '&.MuiTableSortLabel-active': { color: 'white !important' },
                                    '& .MuiTableSortLabel-icon': { color: 'white !important' },
                                  }}
                                >
                                  {label}
                                </TableSortLabel>
                              ) : label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedResults.map((item) => (
                          <TableRow
                            key={item.id}
                            sx={{
                              '&:hover': { backgroundColor: 'rgba(25,118,210,0.04)' },
                              '&:nth-of-type(odd)': { backgroundColor: 'rgba(0,0,0,0.02)' },
                            }}
                          >
                            <TableCell sx={{ wordBreak: 'break-word' }}>
                              {val(item.issue_title)}
                            </TableCell>
                            <TableCell sx={{ wordBreak: 'break-word' }}>
                              {val(item.club_name) || t('district')}
                            </TableCell>
                            <TableCell sx={{ wordBreak: 'break-word' }}>
                              {val(item.event_name)}
                            </TableCell>
                            <TableCell sx={{ wordBreak: 'break-word' }}>
                              {val(item.service_type)}
                            </TableCell>
                            <TableCell sx={{ wordBreak: 'break-word' }}>
                              {val(item.summary)}
                            </TableCell>
                            <TableCell sx={{ textAlign: 'center' }}>
                              <Button
                                component={RouterLink}
                                to={`/pages/${item.id}`}
                                size="small"
                                variant="outlined"
                                sx={{ minWidth: 'auto', px: 1, borderColor: colors.royalBlue, color: colors.royalBlue }}
                              >
                                <OpenInNewIcon fontSize="small" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}

            {/* ── Card View ── */}
            {!isLoading && !error && hasAnyFilter && viewMode === 'page' && (
              <Grid container spacing={3}>
                {pagesData?.data?.results?.map((pageItem) => (
                  <Grid item xs={12} sm={6} md={4} key={pageItem.id}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        transition: 'transform 0.2s',
                        '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 },
                      }}
                    >

                      <CardContent sx={{ flexGrow: 1 }}>
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                          {pageItem.issue_title}
                        </Typography>

                        <Typography variant="body2">{val(pageItem.club_name) || t('district')}</Typography>
                        {pageItem.summary && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.8rem', lineHeight: 1.5 }}>
                            {pageItem.summary}
                          </Typography>
                        )}
                        {pageItem.service_type && (
                          <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {pageItem.service_type.split(',').map((s) => s.trim()).filter(Boolean).map((s) => (
                              <Chip key={s} label={s} size="small" sx={{ fontSize: '0.7rem' }} />
                            ))}
                          </Box>
                        )}
                        <Button
                          component={RouterLink}
                          to={`/pages/${pageItem.id}`}
                          size="small"
                          sx={{ mt: 1, color: colors.royalBlue }}
                          endIcon={<OpenInNewIcon />}
                        >
                          查看
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* ── Photo View ── */}
            {!isLoading && !error && hasAnyFilter && viewMode === 'photo' && (
              <ImageList cols={isMobile ? 2 : 4} gap={8}>
                {photosData?.data?.results?.map((photo) => (
                  <ImageListItem
                    key={photo.id}
                    sx={{ cursor: 'pointer', '&:hover img': { opacity: 0.85 } }}
                    onClick={() => { setSelectedPhoto(photo); setPhotoModalOpen(true); }}
                  >
                    <img
                      src={photo.image}
                      alt={photo.caption || ''}
                      loading="lazy"
                      style={{ borderRadius: 4 }}
                    />
                    {photo.caption && <ImageListItemBar title={photo.caption} />}
                  </ImageListItem>
                ))}
              </ImageList>
            )}

            {/* Pagination */}
            {!isLoading && !error && hasAnyFilter && (() => {
              const totalCount = viewMode === 'photo' ? (photosData?.data?.count || 0) : (pagesData?.data?.count || 0);
              const rawTotalPages = viewMode === 'photo' ? photosData?.data?.total_pages : pagesData?.data?.total_pages;
              const totalPages = rawTotalPages || (totalCount > 0 ? Math.max(1, Math.ceil(totalCount / pageSize)) : 1);
              if (totalCount === 0) return null;
              return (
                <Box sx={{ mt: 4 }}>
                  {/* Per-page + count info */}
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('pageInfo', { page, total: totalPages, count: totalCount })}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">{t('perPage')}</Typography>
                      <select
                        value={pageSize}
                        onChange={(e) => {
                          const s = Number(e.target.value);
                          setPageSize(s);
                          setPage(1);
                          setSearchParams(buildParams(
                            viewMode, searchQuery,
                            selectedYears, selectedServiceTypes,
                            selectedClubs, selectedBeneficiaries, selectedPartners,
                            isExcellent, 1, s
                          ));
                        }}
                        style={{
                          padding: '4px 8px', borderRadius: 6,
                          border: `1px solid ${colors.royalBlue}`,
                          fontSize: 14, color: colors.royalBlue,
                        }}
                      >
                        {[10, 20, 30, 50, 100].map((n) => (
                          <option key={n} value={n}>{n}</option>
                        ))}
                      </select>
                      {t('perPageUnit') && <Typography variant="body2" color="text.secondary">{t('perPageUnit')}</Typography>}
                    </Box>
                  </Box>
                  {/* Page buttons */}
                  {totalPages > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={page === 1}
                        onClick={() => handlePageChange(page - 1)}
                        sx={{ borderColor: colors.royalBlue, color: colors.royalBlue, minWidth: 36 }}
                      >
                        &lt;
                      </Button>
                      {(() => {
                        const pages = [];
                        const delta = 2;
                        const left = Math.max(1, page - delta);
                        const right = Math.min(totalPages, page + delta);
                        if (left > 1) { pages.push(1); if (left > 2) pages.push('...'); }
                        for (let i = left; i <= right; i++) pages.push(i);
                        if (right < totalPages) { if (right < totalPages - 1) pages.push('...'); pages.push(totalPages); }
                        return pages.map((p, i) =>
                          p === '...' ? (
                            <Typography key={`e${i}`} sx={{ px: 1, color: 'text.secondary' }}>…</Typography>
                          ) : (
                            <Button
                              key={p}
                              variant={page === p ? 'contained' : 'outlined'}
                              size="small"
                              onClick={() => handlePageChange(p)}
                              sx={{
                                minWidth: 36,
                                borderColor: colors.royalBlue,
                                color: page === p ? 'white' : colors.royalBlue,
                                backgroundColor: page === p ? colors.royalBlue : 'transparent',
                                fontWeight: page === p ? 700 : 400,
                                '&:hover': { backgroundColor: page === p ? '#1565C0' : 'rgba(25,118,210,0.08)' },
                              }}
                            >
                              {p}
                            </Button>
                          )
                        );
                      })()}
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={page >= totalPages}
                        onClick={() => handlePageChange(page + 1)}
                        sx={{ borderColor: colors.royalBlue, color: colors.royalBlue, minWidth: 36 }}
                      >
                        &gt;
                      </Button>
                    </Box>
                  )}
                </Box>
              );
            })()}
          </Box>
        </Box>
      </Container>

      {/* Photo Modal */}
      <Dialog open={photoModalOpen} onClose={() => setPhotoModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {selectedPhoto?.caption || t('photos')}
          <IconButton onClick={() => setPhotoModalOpen(false)}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedPhoto && (
            <img
              src={selectedPhoto.image}
              alt={selectedPhoto.caption || ''}
              style={{ width: '100%', height: 'auto', borderRadius: 8 }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhotoModalOpen(false)}>{t('close')}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SearchPage;
